import React, { useState, useEffect } from 'react';
import { CustomIcon } from '../UI';
import { defaultFilters } from '../../utils/statsFilterUtils';
import userService from '../../services/userService';
import customerService from '../../services/customerService';
import carrierService from '../../services/carrierService';
import './StatsFilters.scss';

/**
 * Stats Filters Component
 * Schema-driven popover/drawer for advanced filtering
 * Only shows filters enabled for the current tab
 */
const StatsFilters = ({
  isOpen,
  onClose,
  draftFilters,
  onDraftFilterChange,
  filterSchema,
  userRole = 'admin',
  onApply,
  onClear,
  activeTab = 'overview',
}) => {
  const [localFilters, setLocalFilters] = useState(() => ({ ...defaultFilters }));
  const [scopeEmail, setScopeEmail] = useState('');
  const [scopeEmailError, setScopeEmailError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const isEmailInputActiveRef = React.useRef(false);

  const searchByScope = async (query, scopeType) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setScopeEmailError('');
    try {
      let results = [];
      if (scopeType === 'user') {
        const response = await userService.searchUsers(query.trim(), { limit: 10 });
        const data = response?.data?.data ?? response?.data;
        const items = Array.isArray(data) ? data : [];
        results = items.map(u => ({
          id: u._id || u.id,
          email: u.email || '',
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u._id
        }));
      } else if (scopeType === 'customer') {
        const response = await customerService.searchCustomers({ q: query.trim(), limit: 10 });
        const data = response?.data?.data ?? response?.data;
        const items = Array.isArray(data) ? data : [];
        results = items.map(c => ({
          id: c._id || c.id,
          email: c.email || '',
          name: c.companyName || c.email || c._id
        }));
      } else if (scopeType === 'carrier') {
        const response = await carrierService.searchCarriers({ q: query.trim(), limit: 10 });
        const data = response?.data?.data ?? response?.data;
        const items = Array.isArray(data) ? data : [];
        results = items.map(c => ({
          id: c._id || c.id,
          email: c.email || '',
          name: c.name || c.companyName || c.email || c._id
        }));
      }
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setScopeEmailError('Failed to search. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (scopeEmail && scopeEmail.trim().length >= 2 && localFilters.scope && localFilters.scope !== 'global') {
      const debounceTimer = setTimeout(() => {
        searchByScope(scopeEmail, localFilters.scope);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setSearchResults([]);
    }
  }, [scopeEmail, localFilters.scope]);

  const handleScopeSearchChange = (value) => {
    isEmailInputActiveRef.current = true;
    setScopeEmail(value);
    setScopeEmailError('');
    if (!value || value.trim().length < 2) {
      setSearchResults([]);
      handleFilterChange('scopeId', null);
    }
    setTimeout(() => {
      isEmailInputActiveRef.current = false;
    }, 100);
  };

  const handleScopeSelect = (result, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const id = result.id ?? result._id;
    const display = localFilters.scope === 'user' && result.email
      ? result.email
      : (result.name || result.email || '');
    setScopeEmail(display);
    handleFilterChange('scopeId', id ? String(id) : null);
    setSearchResults([]);
  };

  const loadScopeDisplayById = async (id, scopeType) => {
    if (!id || !scopeType || scopeType === 'global') return;
    
    try {
      let display = '';
      if (scopeType === 'user') {
        const response = await userService.getUserById(id);
        const u = response?.data?.data ?? response?.data;
        if (u) {
          display = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '';
        }
      } else if (scopeType === 'customer') {
        const response = await customerService.getCustomerById(id);
        const c = response?.data?.data ?? response?.data;
        if (c) {
          display = c.companyName || c.email || '';
        }
      } else if (scopeType === 'carrier') {
        const response = await carrierService.getCarrierById(id);
        const c = response?.data?.data ?? response?.data;
        if (c) {
          display = c.name || c.companyName || c.email || '';
        }
      }
      if (display) {
        setScopeEmail(display);
      }
    } catch (error) {
      console.error('Load scope display error:', error);
    }
  };

  const prevIsOpenRef = React.useRef(false);
  const prevScopeRef = React.useRef(null);
  const prevScopeIdRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && draftFilters) {
      const wasClosed = !prevIsOpenRef.current;
      const scopeChanged = prevScopeRef.current !== draftFilters.scope;
      const scopeIdChanged = prevScopeRef.current !== null && prevScopeIdRef.current !== draftFilters.scopeId;
      
      setLocalFilters({ ...draftFilters });
      
      if (wasClosed || scopeChanged) {
        if (draftFilters.scopeId && draftFilters.scope && draftFilters.scope !== 'global') {
          loadScopeDisplayById(draftFilters.scopeId, draftFilters.scope);
        } else if (draftFilters.scope === 'global') {
          setScopeEmail('');
          setSearchResults([]);
        } else if (!draftFilters.scopeId) {
          if (!isEmailInputActiveRef.current) {
            setScopeEmail('');
          }
        }
      } else if (scopeIdChanged && draftFilters.scopeId && draftFilters.scope && draftFilters.scope !== 'global') {
        if (!isEmailInputActiveRef.current) {
          loadScopeDisplayById(draftFilters.scopeId, draftFilters.scope);
        }
      }
      
      prevIsOpenRef.current = true;
      prevScopeRef.current = draftFilters.scope;
      prevScopeIdRef.current = draftFilters.scopeId;
    } else if (!isOpen) {
      setScopeEmail('');
      setSearchResults([]);
      setScopeEmailError('');
      isEmailInputActiveRef.current = false;
      prevIsOpenRef.current = false;
      prevScopeRef.current = null;
      prevScopeIdRef.current = null;
    }
  }, [isOpen, draftFilters]);

  if (!isOpen) return null;

  const handleFilterChange = (key, value) => {
    const updated = {
      ...localFilters,
      [key]: value
    };
    setLocalFilters(updated);
    onDraftFilterChange?.(key, value);
  };

  const handleApply = async () => {
    if (localFilters.scope && localFilters.scope !== 'global' && scopeEmail && scopeEmail.trim().length >= 2 && !localFilters.scopeId) {
      setScopeEmailError('Please select a result from the search list');
      return;
    }
    onApply && onApply();
    onClose();
  };

  const handleClear = () => {
    const cleared = { ...defaultFilters };
    setLocalFilters(cleared);
    setScopeEmail('');
    setSearchResults([]);
    setScopeEmailError('');
    onClear && onClear();
  };

  // Load status options
  const loadStatusOptions = [
    { value: 'listed', label: 'Listed' },
    { value: 'dispatched', label: 'Dispatched' },
    { value: 'pickedUp', label: 'Picked Up' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'onHold', label: 'On Hold' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'expired', label: 'Expired' }
  ];

  const receivablePaymentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'received', label: 'Received' },
    { value: 'partially received', label: 'Partially Received' },
    { value: 'withheld', label: 'Withheld' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'on Hold', label: 'On Hold' },
    { value: 'pay today', label: 'Pay Today' }
  ];
  const payablePaymentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'paid', label: 'Paid' },
    { value: 'partially paid', label: 'Partially Paid' },
    { value: 'withheld', label: 'Withheld' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'on Hold', label: 'On Hold' },
    { value: 'pay today', label: 'Pay Today' }
  ];
  const paymentStatusOptions = activeTab === 'receivable' ? receivablePaymentStatusOptions : activeTab === 'payable' ? payablePaymentStatusOptions : receivablePaymentStatusOptions;

  const periodOptions = [
    { value: 'day', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];
  
  const shipmentTypeOptions = [
    { value: 'freight', label: 'Freight' },
    { value: 'vehicle', label: 'Vehicle' }
  ];
  const scopeRoles = [
    'admin',
    'manager',
    'accountingManager',
    'accountingIn',
    'accountingOut',
    'freightBroker',
    'dispatcher',
    'Pre-dispatcher',
    'partner',
    'salesAgent',
    'bidAgent'
  ];

  return (
    <div className="stats-filters-overlay">
      <div className="stats-filters-panel" onClick={(e) => e.stopPropagation()}>
        <div className="stats-filters-panel__header">
          <h3>{activeTab === 'loads' ? 'Filter Loads Statistics' : activeTab === 'users' ? 'Filter Users Statistics' : activeTab === 'receivable' ? 'Filter Receivable Statistics' : activeTab === 'payable' ? 'Filter Payable Statistics' : activeTab === 'carriers' ? 'Filter Carriers Statistics' : activeTab === 'customers' ? 'Filter Customers Statistics' : 'Filter Statistics'}</h3>
          <button
            type="button"
            className="stats-filters-panel__close"
            onClick={onClose}
          >
            <CustomIcon name="close" size={20} />
          </button>
        </div>

        <div className="stats-filters-panel__body">
          {(activeTab === 'loads' || activeTab === 'users' || activeTab === 'receivable' || activeTab === 'payable') ? (
            <>
              <div className="stats-filters-panel__field">
                <label>Date Period</label>
                <select
                  value={localFilters.period || 'month'}
                  onChange={(e) => {
                    handleFilterChange('period', e.target.value);
                    if (e.target.value !== 'custom') {
                      handleFilterChange('startDate', null);
                      handleFilterChange('endDate', null);
                    }
                  }}
                  className="stats-filters-panel__select"
                >
                  {periodOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {localFilters.period && localFilters.period !== 'custom' && (
                  <div className="stats-filters-panel__period-info">
                    <small className="stats-filters-panel__field__hint">
                      Showing data for: <strong>{periodOptions.find(opt => opt.value === localFilters.period)?.label || localFilters.period}</strong>
                    </small>
                  </div>
                )}
                {localFilters.period === 'custom' && (
                  <small className="stats-filters-panel__field__hint">Select custom date range below</small>
                )}
                {!localFilters.period && (
                  <small className="stats-filters-panel__field__hint">Select the date period for statistics</small>
                )}
              </div>

              {localFilters.period === 'custom' && (
                <>
                  <div className="stats-filters-panel__field">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={localFilters.startDate ? localFilters.startDate.split('T')[0] : ''}
                      onChange={(e) => handleFilterChange('startDate', e.target.value ? `${e.target.value}T00:00:00.000Z` : null)}
                      className="stats-filters-panel__input"
                    />
                  </div>
                  <div className="stats-filters-panel__field">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={localFilters.endDate ? localFilters.endDate.split('T')[0] : ''}
                      onChange={(e) => handleFilterChange('endDate', e.target.value ? `${e.target.value}T23:59:59.999Z` : null)}
                      className="stats-filters-panel__input"
                    />
                  </div>
                </>
              )}

              {filterSchema?.scope && scopeRoles.includes(userRole) && (
                <>
                  <div className="stats-filters-panel__field">
                    <label>Filter By</label>
                    <select
                      value={!localFilters.scope || localFilters.scope === 'global' ? 'all' : localFilters.scope}
                      onChange={(e) => {
                        const scopeValue = e.target.value === 'all' ? 'global' : e.target.value;
                        handleFilterChange('scope', scopeValue);
                        if (e.target.value === 'all') {
                          handleFilterChange('scopeId', null);
                          setScopeEmail('');
                          setSearchResults([]);
                        }
                      }}
                      className="stats-filters-panel__select"
                    >
                      <option value="all">
                        {activeTab === 'users' ? 'All Users' : activeTab === 'receivable' || activeTab === 'payable' ? 'All' : 'All Loads'}
                      </option>
                      <option value="user">By User</option>
                      {(activeTab === 'loads' || activeTab === 'receivable' || activeTab === 'payable') && (
                        <option value="customer">By Customer</option>
                      )}
                      {(activeTab === 'loads' || activeTab === 'payable') && (
                        <option value="carrier">By Carrier</option>
                      )}
                    </select>
                    <small className="stats-filters-panel__field__hint">
                      {activeTab === 'users' ? 'Filter statistics by specific user' : activeTab === 'receivable' || activeTab === 'payable' ? 'Filter payments by specific user, customer, or carrier' : 'Filter loads by specific user, customer, or carrier'}
                    </small>
                  </div>

                  {localFilters.scope && localFilters.scope !== 'global' && (
                    <div className="stats-filters-panel__field">
                      <label>Search {localFilters.scope.charAt(0).toUpperCase() + localFilters.scope.slice(1)}</label>
                      <div className="stats-filters-panel__email-search">
                        <input
                          type="text"
                          value={scopeEmail}
                          onChange={(e) => handleScopeSearchChange(e.target.value)}
                          placeholder={localFilters.scope === 'user' ? 'Search by email or name' : localFilters.scope === 'customer' ? 'Search by company name or email' : 'Search by name, company, MC#, or email'}
                          className={`stats-filters-panel__input ${scopeEmailError ? 'stats-filters-panel__input--error' : ''}`}
                        />
                        {isSearching && (
                          <div className="stats-filters-panel__search-loading">Searching...</div>
                        )}
                        {searchResults.length > 0 && (
                          <div className="stats-filters-panel__search-results">
                            {searchResults.map((result) => (
                              <div
                                key={result.id ?? result._id}
                                className="stats-filters-panel__search-result-item"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleScopeSelect(result, e);
                                }}
                              >
                                <div className="stats-filters-panel__search-result-name">{result.name}</div>
                                {result.email && (
                                  <div className="stats-filters-panel__search-result-email">{result.email}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {scopeEmailError && (
                          <small className="stats-filters-panel__field__hint stats-filters-panel__field__hint--error">
                            {scopeEmailError}
                          </small>
                        )}
                        {!scopeEmailError && !isSearching && !scopeEmail && (
                          <small className="stats-filters-panel__field__hint">Type to search for {localFilters.scope}</small>
                        )}
                      </div>
                    </div>
                  )}

                  {(activeTab === 'receivable' || activeTab === 'payable') && filterSchema?.paymentStatus && (
                    <div className="stats-filters-panel__field">
                      <label>Payment Status</label>
                      <div className="stats-filters-panel__multiselect">
                        {paymentStatusOptions.map(option => {
                          const isSelected = Array.isArray(localFilters.paymentStatus) && localFilters.paymentStatus.includes(option.value);
                          return (
                            <label key={option.value} className="stats-filters-panel__checkbox-label">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const current = Array.isArray(localFilters.paymentStatus) ? localFilters.paymentStatus : [];
                                  const updated = e.target.checked
                                    ? [...current, option.value]
                                    : current.filter(v => v !== option.value);
                                  handleFilterChange('paymentStatus', updated);
                                }}
                                className="stats-filters-panel__checkbox"
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                      <small className="stats-filters-panel__field__hint">Show only payments with selected statuses</small>
                    </div>
                  )}
                  {activeTab === 'loads' && filterSchema?.loadStatus && (
                    <div className="stats-filters-panel__field">
                      <label>Load Status</label>
                      <div className="stats-filters-panel__multiselect">
                        {loadStatusOptions.map(option => {
                          const isSelected = Array.isArray(localFilters.loadStatus) && localFilters.loadStatus.includes(option.value);
                          return (
                            <label key={option.value} className="stats-filters-panel__checkbox-label">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const current = Array.isArray(localFilters.loadStatus) ? localFilters.loadStatus : [];
                                  const updated = e.target.checked
                                    ? [...current, option.value]
                                    : current.filter(v => v !== option.value);
                                  handleFilterChange('loadStatus', updated);
                                }}
                                className="stats-filters-panel__checkbox"
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                      <small className="stats-filters-panel__field__hint">Show only loads with selected statuses</small>
                    </div>
                  )}
                  {activeTab === 'loads' && filterSchema?.shipmentType && (
                    <div className="stats-filters-panel__field">
                      <label>Shipment Type</label>
                      <select
                        value={localFilters.shipmentType || ''}
                        onChange={(e) => handleFilterChange('shipmentType', e.target.value || null)}
                        className="stats-filters-panel__select"
                      >
                        <option value="">All Types</option>
                        {shipmentTypeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {filterSchema?.period && (
                <div className="stats-filters-panel__field">
                  <label>Period</label>
                  <select
                    value={localFilters.period || 'month'}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                    className="stats-filters-panel__select"
                  >
                    {periodOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {filterSchema?.period && localFilters.period === 'custom' && (
                <>
                  <div className="stats-filters-panel__field">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={localFilters.startDate ? localFilters.startDate.split('T')[0] : ''}
                      onChange={(e) => handleFilterChange('startDate', e.target.value ? `${e.target.value}T00:00:00.000Z` : null)}
                      className="stats-filters-panel__input"
                    />
                  </div>
                  <div className="stats-filters-panel__field">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={localFilters.endDate ? localFilters.endDate.split('T')[0] : ''}
                      onChange={(e) => handleFilterChange('endDate', e.target.value ? `${e.target.value}T23:59:59.999Z` : null)}
                      className="stats-filters-panel__input"
                    />
                  </div>
                </>
              )}

              {filterSchema?.scope && scopeRoles.includes(userRole) && (
                <div className="stats-filters-panel__field">
                  <label>Scope</label>
                  <select
                    value={localFilters.scope || 'global'}
                    onChange={(e) => {
                      const newScope = e.target.value;
                      handleFilterChange('scope', newScope);
                      handleFilterChange('scopeId', null);
                      if (newScope === 'global') {
                        setScopeEmail('');
                        setSearchResults([]);
                      }
                    }}
                    className="stats-filters-panel__select"
                  >
                    <option value="global">Global</option>
                    <option value="user">User</option>
                    <option value="customer">Customer</option>
                    <option value="carrier">Carrier</option>
                  </select>
                </div>
              )}

              {filterSchema?.scope && filterSchema?.scopeId && localFilters.scope && localFilters.scope !== 'global' && (
                <div className="stats-filters-panel__field">
                  <label>Search {localFilters.scope.charAt(0).toUpperCase() + localFilters.scope.slice(1)}</label>
                  <div className="stats-filters-panel__email-search">
                    <input
                      type="text"
                      value={scopeEmail}
                      onChange={(e) => handleScopeSearchChange(e.target.value)}
                      placeholder={localFilters.scope === 'user' ? 'Search by email or name' : localFilters.scope === 'customer' ? 'Search by company name or email' : 'Search by name, company, MC#, or email'}
                      className={`stats-filters-panel__input ${scopeEmailError ? 'stats-filters-panel__input--error' : ''}`}
                    />
                    {isSearching && (
                      <div className="stats-filters-panel__search-loading">Searching...</div>
                    )}
                    {searchResults.length > 0 && (
                      <div className="stats-filters-panel__search-results">
                        {searchResults.map((result) => (
                          <div
                            key={result.id ?? result._id}
                            className="stats-filters-panel__search-result-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleScopeSelect(result, e);
                            }}
                          >
                            <div className="stats-filters-panel__search-result-name">{result.name}</div>
                            {result.email && (
                              <div className="stats-filters-panel__search-result-email">{result.email}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {scopeEmailError && (
                      <small className="stats-filters-panel__field__hint stats-filters-panel__field__hint--error">
                        {scopeEmailError}
                      </small>
                    )}
                    {!scopeEmailError && !isSearching && !scopeEmail && (
                      <small className="stats-filters-panel__field__hint">Type to search by email or name</small>
                    )}
                  </div>
                </div>
              )}

              {filterSchema?.loadStatus && (
                <div className="stats-filters-panel__field">
                  <label>Load Status</label>
                  <div className="stats-filters-panel__multiselect">
                    {loadStatusOptions.map(option => {
                      const isSelected = Array.isArray(localFilters.loadStatus) && localFilters.loadStatus.includes(option.value);
                      return (
                        <label key={option.value} className="stats-filters-panel__checkbox-label">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const current = Array.isArray(localFilters.loadStatus) ? localFilters.loadStatus : [];
                              const updated = e.target.checked
                                ? [...current, option.value]
                                : current.filter(v => v !== option.value);
                              handleFilterChange('loadStatus', updated);
                            }}
                            className="stats-filters-panel__checkbox"
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {filterSchema?.paymentType && (
                <div className="stats-filters-panel__field">
                  <label>Payment Type</label>
                  <select
                    value={localFilters.paymentType || ''}
                    onChange={(e) => handleFilterChange('paymentType', e.target.value || null)}
                    className="stats-filters-panel__select"
                  >
                    <option value="">All Types</option>
                    <option value="receivable">Receivable</option>
                    <option value="payable">Payable</option>
                  </select>
                </div>
              )}

              {filterSchema?.paymentStatus && (
                <div className="stats-filters-panel__field">
                  <label>Payment Status</label>
                  <div className="stats-filters-panel__multiselect">
                    {paymentStatusOptions.map(option => {
                      const isSelected = Array.isArray(localFilters.paymentStatus) && localFilters.paymentStatus.includes(option.value);
                      return (
                        <label key={option.value} className="stats-filters-panel__checkbox-label">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const current = Array.isArray(localFilters.paymentStatus) ? localFilters.paymentStatus : [];
                              const updated = e.target.checked
                                ? [...current, option.value]
                                : current.filter(v => v !== option.value);
                              handleFilterChange('paymentStatus', updated);
                            }}
                            className="stats-filters-panel__checkbox"
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="stats-filters-panel__footer">
          <button
            type="button"
            className="stats-filters-panel__button stats-filters-panel__button--clear"
            onClick={handleClear}
          >
            Clear All
          </button>
          <button
            type="button"
            className="stats-filters-panel__button stats-filters-panel__button--apply"
            onClick={handleApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatsFilters;
